<!-- @catalog CInput with a microphone that dictates spoken text into the field, for feedback fields that take AI suggestions. -->
<template>
  <div
    class="c-voice-input"
    :class="{ 'c-voice-input--actions': actionsVisible }"
  >
    <CInput v-bind="$attrs" ref="cInputRef" v-model="displayModel" />

    <!-- The field's action row, hanging under the box so the field itself
         keeps its height: whatever the caller puts in `#actions` (AI buttons)
         followed by the microphone.
         v-show, not v-if: the microphone owns the recording and must outlive a
         blur. `.prevent` on the press keeps the focus in the field, so it does
         not collapse under the cursor before the click lands.
         `ai-suggestions-panels` keeps AISuggestions' click-outside from
         closing on a press in this row. -->
    <div
      v-show="actionsVisible"
      class="c-voice-input__actions ai-suggestions-panels"
      @mousedown.prevent
    >
      <slot name="actions" />
      <VoiceInputButton
        :disable="voiceDisabled"
        :voice-label="voiceLabel"
        @transcript="applyTranscript"
        @recording="isRecording = $event"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useEventListener } from "@vueuse/core";
import {
  computed,
  nextTick,
  onMounted,
  ref,
  useAttrs,
  useTemplateRef,
  type ComponentPublicInstance,
} from "vue";
import CInput from "shared/components/wrappers/input/CInput.vue";
import VoiceInputButton from "shared/components/wrappers/input/VoiceInputButton.vue";

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /** Whose feedback this field holds, shown in the recording indicator. */
    voiceLabel?: string;
    /** Shows the action row (AI buttons + microphone); the caller owns focus. */
    showActions?: boolean;
  }>(),
  { voiceLabel: "", showActions: false },
);

const emit = defineEmits<{
  /**
   * The live field value, on every keystroke. `update:model-value` is debounced
   * (it autosaves, so it waits for a pause in the typing), which makes it the
   * wrong thing to watch for anything that has to keep up with the screen — such
   * as a button that appears at a character count.
   */
  typing: [value: string];
}>();

const isRecording = ref(false);
const actionsVisible = computed(
  () => props.showActions === true || isRecording.value === true,
);

const model = defineModel<string | number | null>({ required: true });

const attrs = useAttrs();
const cInputRef = useTemplateRef("cInputRef");

/** The native control q-input renders; it owns the live value. */
const fieldElement = computed<HTMLInputElement | HTMLTextAreaElement | null>(
  () =>
    (
      cInputRef.value?.inputRef as ComponentPublicInstance | null | undefined
    )?.$el?.querySelector?.("textarea, input") ?? null,
);

// Listened for on the native control: the event bubbles out of it whatever
// Quasar does with its own listeners, and it is the only signal that is not
// delayed by `debounce`. Attached on mount, so the very first keystroke counts.
onMounted(() => {
  useEventListener(fieldElement.value, "input", (event: Event) => {
    emit(
      "typing",
      String((event.target as HTMLInputElement | null)?.value ?? ""),
    );
  });
});

// Dictation writes to the model directly, which bypasses the input element —
// so a field the user cannot type into must not accept speech either. A bare
// `disable` / `readonly` attribute arrives as "" rather than true, and both
// forms have to gate the button.
const voiceDisabled = computed(() => {
  const isSet = (value: unknown) => value === true || value === "";

  return isSet(attrs.disable) || isSet(attrs.readonly);
});

// While dictating, the field shows the running transcript without pushing every
// partial to the parent: those writes autosave, and a spoken sentence would
// otherwise fire a request per second. Only the closing transcript is written
// to the model.
const liveTranscript = ref<string | null>(null);

const displayModel = computed({
  get: () =>
    liveTranscript.value !== null ? liveTranscript.value : model.value,
  set: (value) => {
    liveTranscript.value = null;
    model.value = value;
  },
});

/**
 * Replace the field's whole content the way a paste would: through the
 * browser's own editing pipeline.
 *
 * Assigning to the model is invisible to the undo stack, which only records
 * edits the browser itself made — so a teacher who accepts an AI suggestion can
 * never press cmd+z to get their own sentence back. `insertText` is deprecated
 * but it is still the only API that writes onto that stack; when it is refused
 * the plain assignment keeps the feature working, just without undo.
 */
const replaceValueAsUserEdit = (value: string) => {
  const element = fieldElement.value;

  if (element !== null && typeof document.execCommand === "function") {
    try {
      element.focus();
      // `select()` rather than `setSelectionRange`: the latter raises
      // InvalidStateError on number, email and date inputs.
      element.select();
      document.execCommand("insertText", false, value);
    } catch {
      // Fall through to the plain assignment below.
    }

    // The return value of `execCommand` is not to be trusted: it reports
    // success on browsers that insert nothing. The field itself is the only
    // honest answer, and this write must never silently do nothing — it is
    // what the teacher clicked.
    if (element.value === value) {
      return;
    }
  }

  model.value = value;
};

/**
 * Add text to what the field already holds, the way dictation does: an AI
 * suggestion is something extra, not a replacement of what the teacher wrote.
 *
 * The base comes from the element rather than the model: the field autosaves on
 * a debounce, so the model can still be a pause behind what is on screen.
 */
const appendValueAsUserEdit = (value: string) => {
  const base = String(fieldElement.value?.value ?? model.value ?? "");

  replaceValueAsUserEdit(base.length > 0 ? `${base} ${value}` : value);
};

// Dictated text is appended rather than assigned, so speaking twice builds up
// one note and never wipes what the user already typed.
//
// The base is read here rather than snapshotted when recording started: only
// `liveTranscript` moves while dictating, so the model still holds it — and on
// the upload path the transcript lands seconds after the recording stopped,
// long enough for the teacher to have typed something that a snapshot would
// silently overwrite.
const applyTranscript = async (text: string, isFinal: boolean) => {
  const base = String(model.value ?? "");
  const next = base.length > 0 ? `${base} ${text}` : text;

  if (isFinal === true) {
    // Put the field back to what it held before dictation started, and only
    // then write the result through the browser's own editing pipeline. Every
    // partial above was written straight to the value, which the undo stack
    // never sees — and which clears it outright in Chrome. Without the restore
    // the single undoable edit would sit on top of the last preview, so ctrl+z
    // handed back a half-finished sentence instead of the teacher's own text.
    liveTranscript.value = base;
    await nextTick();

    liveTranscript.value = null;
    replaceValueAsUserEdit(next);
    return;
  }

  liveTranscript.value = next;
};

defineExpose({
  replaceValueAsUserEdit,
  appendValueAsUserEdit,
});
</script>

<style lang="scss" scoped>
.c-voice-input {
  @apply tw-relative;

  // Absolute, so showing it never pushes the rows below down. Spans the
  // field's width so an AI panel opened from it can too; the row itself lets
  // clicks through, only its buttons take them.
  &__actions {
    @apply tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-full tw-z-20 tw-mt-1 tw-flex tw-items-center tw-gap-1;

    > :deep(*) {
      @apply tw-pointer-events-auto;
    }

    // Every button in the row is the same small pill, the microphone and the
    // AI triggers alike.
    :deep(.q-btn) {
      // !important: CButton's own height and font size out-specify these.
      height: 24px !important;
      padding: 0 8px 0 6px !important;
      font-size: 11px !important;

      @apply tw-min-h-0 tw-rounded-full tw-border tw-border-solid tw-border-gray-200 tw-bg-white tw-font-medium tw-shadow-sm;

      .q-btn__content {
        font-size: 11px !important;
        line-height: 14px;
      }

      .q-icon {
        @apply tw-mr-1;

        font-size: 12px !important;
      }
    }
  }

  // The focus halo around the box while the row is open.
  &--actions :deep(.q-field__control) {
    box-shadow: 0 0 0 3px rgb(var(--color-aqua-500) / 0.2);
  }
}
</style>
